import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import LogoHead from "../../assets/images/logo-head-color.png";
import {
  ShoppingBag,
  CalendarCheck,
  Users,
  CircleDollarSign,
  Calculator,
  BookUser,
  Ticket,
  Gift,
  Scan,
  SquareChevronRight,
  SquareChevronDown,
  SquareChevronLeft,
  LogOut,
  Speech,
  LucideIcon,
  TabletSmartphone,
  Landmark
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";

interface MenuItem {
  name: string;
  path: string;
  icon: LucideIcon;
  children?: MenuItem[];
}

export default function Sidebar() {
  const { logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [collapsed, setCollapsed] = useState(false);
  const [openMenus, setOpenMenus] = useState<string[]>([]);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const menuItems: MenuItem[] = [
    { name: "Users", path: "/users", icon: Users },
    {
      name: "Events",
      path: "/events",
      icon: CalendarCheck,
      children: [
        { name: "Event List", path: "/events", icon: CalendarCheck },
        { name: "Tickets", path: "/tickets-info", icon: Ticket },
        { name: "Promo Codes", path: "/promo-codes", icon: Gift },
        { name: "Guest List", path: "/guest-list", icon: BookUser },
        {
          name: "Matched Users",
          path: "/matched-requested-users",
          icon: Users,
        },
        {
          name: "Purchased Tickets",
          path: "/events-purchased-tickets",
          icon: ShoppingBag,
        },
        { name: "Refund Requests", path: "/refunds", icon: CircleDollarSign },
        { name: "Scanned Tickets", path: "/scanned-tickets", icon: Scan },
      ],
    },
    { name: "Payouts", path: "/events-payouts", icon: CircleDollarSign },
    { name: "Host List", path: "/host-list", icon: Speech },
    {
      name: "Host Fee (Custom)",
      path: "/custom-fee-details",
      icon: Calculator,
    },
    {
      name: "Tax Management",
      path: "/tax-management",
      icon: Landmark,
    },
    {
      name: "Mobile Versioning",
      path: "/mobile-versioning",
      icon: TabletSmartphone,
    },
  ];

  const toggleMenu = (name: string) => {
    setOpenMenus((prev) =>
      prev.includes(name) ? prev.filter((x) => x !== name) : [...prev, name]
    );
  };

  // Recursive active check
  const isMenuActive = (item: MenuItem, currentPath: string): boolean => {
    if (item.path === currentPath) return true;
    if (item.children)
      return item.children.some((child) => isMenuActive(child, currentPath));
    return false;
  };

  // Open parents automatically if a child is active
  useEffect(() => {
    const openActiveParents = (items: MenuItem[], path: string) => {
      for (let item of items) {
        if (isMenuActive(item, path)) {
          if (item.children)
            setOpenMenus((prev) => Array.from(new Set([...prev, item.name])));
          if (item.children) openActiveParents(item.children, path);
        }
      }
    };
    openActiveParents(menuItems, location.pathname);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

const renderMenuItem = (item: MenuItem, level: number = 0) => {
  const active = isMenuActive(item, location.pathname);
  const hasChildren = !!item.children?.length;
  const isOpen = hasChildren && openMenus.includes(item.name);

  // ---------------- COLLAPSED MODE ----------------
  if (collapsed) {
    return (
      <div key={item.name} className="relative group overflow-visible">
        <div
          className={`flex justify-center items-center p-2 rounded cursor-pointer hover:bg-gray-700
            ${active ? "bg-gray-700 border-l-4 border-[#EBA24D]" : "border-l-4 border-transparent"}`}
          onClick={() => !hasChildren && navigate(item.path)} // only navigate on leaf items
        >
          <item.icon className="w-5 h-5" />
        </div>

        {/* Tooltip */}
        <span
          className="absolute left-16 top-1/2 transform -translate-y-1/2 bg-black text-white text-xs px-2 py-1 rounded opacity-0
            group-hover:opacity-100 transition-all duration-200 z-50 whitespace-nowrap shadow-lg"
        >
          {item.name}
        </span>

        {/* Render children icons (only one level, no nested expansion) */}
        {item.children?.map((child) =>
          renderMenuItem({ ...child, children: undefined })
        )}
      </div>
    );
  }

  // ---------------- EXPANDED MODE ----------------
  return (
    <div key={item.name} className="relative group flex flex-col">
      <div
        className={`flex items-center p-2 mt-1 rounded cursor-pointer hover:bg-gray-700 transition
          ${level > 0 ? "pl-4" : ""}
          ${active ? "bg-gray-700 border-l-4 border-[#EBA24D]" : "border-l-4 border-transparent"}`}
        onClick={() => (hasChildren ? toggleMenu(item.name) : navigate(item.path))}
      >
        <item.icon className="w-5 h-5" />
        <span className="ml-2 text-sm">{item.name}</span>
        {hasChildren && (
          <span className="ml-auto">
            {isOpen ? <SquareChevronDown className="w-4" /> : <SquareChevronRight className="w-4" />}
          </span>
        )}
      </div>

      {/* Nested children */}
      {hasChildren && isOpen && (
        <div className="ml-4 border-l border-gray-700 overflow-hidden transition-all duration-300 ease-in-out max-h-96">
          {item.children!.map((child) => renderMenuItem(child, level + 1))}
        </div>
      )}
    </div>
  );
};


  return (
    <aside
      className={`bg-gray-800 text-white flex-shrink-0 min-h-screen flex flex-col transition-all duration-300
        ${collapsed ? "w-16" : "w-64"}`}
    >
      {/* Header */}
      <div className="flex items-center py-4 px-1 border-b border-gray-700 relative">
        <img
          src={LogoHead}
          alt="Logo"
          className={`rounded transition-all duration-300 ${
            collapsed ? "w-8 h-8" : "w-10 h-10"
          }`}
        />
        {!collapsed && (
          <span className="ml-2 text-lg font-bold">Pinyada Admin</span>
        )}

        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className={`${
            !collapsed
              ? "absolute right-2  text-gray-300 hover:text-white"
              : "mx-1 bg-[#71288d] text-white rounded hover:bg-[#71288e]"
          }`}
        >
          {collapsed ? (
            <SquareChevronRight
              className={`${!collapsed ? "w-5 h-5" : "w-5 h-5 "}`}
            />
          ) : (
            <SquareChevronLeft className="w-5 h-5" />
          )}
        </button>
      </div>

      {/* Menu */}
      <div className="flex-1 scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800">
        <nav className="flex flex-col p-2  space-y-1">
          {menuItems.map((item) => renderMenuItem(item))}
        </nav>
      </div>

      {/* Bottom logout & time */}
      <div className="p-2 border-t border-gray-700 bg-gray-800 relative group">
        {!collapsed && (
          <div className="text-[0.8rem] text-gray-300 mb-2">
            {currentTime.toLocaleString(undefined, {
              weekday: "short",
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
              hour12: true,
            })}
          </div>
        )}

        <div
          className={`flex items-center p-2 rounded hover:bg-red-600 cursor-pointer transition
            ${collapsed ? "justify-center" : "space-x-2"}`}
          onClick={handleLogout}
        >
          <LogOut className="w-5 h-5" />
          {!collapsed && <span className="text-[0.8rem]">Logout</span>}
        </div>

        {collapsed && (
          <span
            className="absolute left-14 bottom-3 bg-black text-white text-xs px-2 py-1 rounded opacity-0
            group-hover:opacity-100 whitespace-nowrap transition"
          >
            Logout
          </span>
        )}
      </div>
    </aside>
  );
}
